'use client';
import { ModalID, useModal } from '@repo/ui/src/modules/UI';
import useTranslation from '../../context/Translation/useTranslation';
import { classNames } from '@repo/ui/src/utils/classNames';
import { useIsSM, useIsLG } from '@repo/ui/src/utils/useMediaQuery';
import Button from '@repo/ui/src/components/Button';
import YoutubePlayIcon from '@repo/ui/src/components/Icons/YoutubePlayIcon';
import { AmplienceButtonProps, AlignH, ButtonSizes } from '@repo/ui/src/types';
import { Anchor } from '@repo/ui/src/components/Anchor';
import { getButtonVariant } from '@repo/ui/src/components/Button/utils';
import GrillRecommendationButton from '@repo/ui/src/components/GrillRecommendationButton';

interface Props {
  buttons: Array<AmplienceButtonProps>;
  align: AlignH;
  buttonSize?: ButtonSizes;
  buttonNegative?: boolean;
  dark?: boolean;
  grillRecommendation?: any;
  youtubeLink?: any;
  className?: string;
}

const getAlignmentClass = (alignment: AlignH | undefined) => {
  if (alignment === 'left') {
    return 'items-start justify-start';
  }
  if (alignment === 'right') {
    return 'items-end justify-end';
  }
  return 'items-center justify-center';
};

export const ToutContentButtons = ({
  buttons,
  align,
  buttonSize,
  buttonNegative = false,
  dark,
  grillRecommendation,
  youtubeLink,
  className,
}: Props) => {
  const { push } = useModal(ModalID.video);
  const smAndUp = useIsSM();
  const lgAndUp = useIsLG();
  const { t } = useTranslation();

  if (!buttons?.length && !grillRecommendation && !youtubeLink) return null;

  return (
    <div
      className={classNames(
        'flex gap-x-6 gap-y-2 flex-wrap flex-column sm:flex-row sm:items-center',
        getAlignmentClass(align),
        className,
      )}
    >
      {buttons?.map(
        (b, i) =>
          b?.link?.displayName && (
            <div
              key={i}
              className={classNames(
                'mb-0.5 only:mb-0 sm:mb-px last:mb-px',
                align === 'right' && 'sm:first:ml-1.5 sm:last:mr-0 sm:mr-1.5',
              )}
            >
              <Button
                {...b?.link}
                as={Anchor}
                dark={dark}
                size={buttonSize}
                variant={getButtonVariant(b.style, b.icon)}
                negative={buttonNegative}
                className={b?.style === 'Text & Icon' ? '!px-0' : ''}
              >
                {b?.link?.displayName}
              </Button>
            </div>
          ),
      )}
      <GrillRecommendationButton
        grillRecommendation={grillRecommendation}
        dark={dark}
        buttonSize={buttonSize}
      />
      {youtubeLink && (
        <button
          className="p-0 ml-[-6px] mt-[-12px] text-white transition-colors hover:text-brand-300 focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-brand-300"
          aria-label={t('genericContent.videoPlayButtonLabel', undefined, {
            default: 'Open Video Dialog',
          })}
          onClick={() =>
            push({
              youtubeLink,
              playlistItems: youtubeLink?.associatedPlaylist?.body,
            })
          }
        >
          <YoutubePlayIcon
            height={lgAndUp ? '80px' : smAndUp ? '64px' : '56px'}
            shadow
            uniqueId={youtubeLink.youtubeId}
          />
        </button>
      )}
    </div>
  );
};
